import { FC, useEffect } from 'react'

import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'

import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { AddClientBody, Client } from 'src/Redux/api/clientsApi.ts'
import { Modal } from 'src/Components/Modal'
import { organizationalFormOptions } from 'src/Helpers/organizationalForm'
import { Button } from 'src/Components/Button'

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  padding: 20px 0;
  width: 100%;
`

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

interface AddClientModalProps {
  onClose: () => void
  onEditClient: (client: AddClientBody) => void
  isOpened: boolean
  clientToEdit: Client | null
}

export const EditClientsModal: FC<AddClientModalProps> = ({
  isOpened,
  onClose,
  onEditClient,
  clientToEdit,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddClientBody>({
    defaultValues: {
      name: clientToEdit?.name || '',
      inn: clientToEdit?.inn || '',
      director: clientToEdit?.director || '',
      legalAddress: clientToEdit?.legalAddress || '',
      phone: clientToEdit?.phone || '',
      email: clientToEdit?.email || '',
      organizationalForm: clientToEdit?.organizationalForm || '',
      ogrn: clientToEdit?.ogrn || undefined,
      headPosition: clientToEdit?.headPosition || '',
      headPositionGenitive: clientToEdit?.headPositionGenitive || '',
      headNameGenitive: clientToEdit?.headNameGenitive || '',
      offerContactName: clientToEdit?.offerContactName || '',
      offerContactPosition: clientToEdit?.offerContactPosition || '',
      authorityType: clientToEdit?.authorityType || 'Устава',
      kpp: clientToEdit?.kpp || '',
      rs: clientToEdit?.rs || '',
      ks: clientToEdit?.ks || '',
      bic: clientToEdit?.bic || '',
      bankName: clientToEdit?.bankName || '',
      postAddress: clientToEdit?.postAddress || '',
    },
  })

  const onSubmit = (data: AddClientBody): void => {
    const processedData = {
      ...data,
      authorityType: data.authorityType
        ? data.authorityType.charAt(0).toUpperCase() +
          data.authorityType.slice(1)
        : 'Устава',
    }
    onEditClient(processedData)
    reset()
    onClose()
  }

  useEffect(() => {
    if (clientToEdit) {
      reset({
        name: clientToEdit.name,
        inn: clientToEdit.inn,
        director: clientToEdit.director,
        legalAddress: clientToEdit.legalAddress,
        phone: clientToEdit.phone,
        email: clientToEdit.email,
        organizationalForm: clientToEdit.organizationalForm,
        ogrn: clientToEdit.ogrn,
        headPosition: clientToEdit.headPosition,
        headPositionGenitive: clientToEdit.headPositionGenitive,
        headNameGenitive: clientToEdit.headNameGenitive,
        offerContactName: clientToEdit.offerContactName,
        offerContactPosition: clientToEdit.offerContactPosition,
        authorityType: clientToEdit.authorityType || 'Устава',
        kpp: clientToEdit.kpp,
        rs: clientToEdit.rs,
        ks: clientToEdit.ks,
        bic: clientToEdit.bic,
        bankName: clientToEdit.bankName,
        postAddress: clientToEdit.postAddress,
      })
    }
  }, [clientToEdit, reset])

  return (
    <Modal opened={isOpened} onClose={onClose} title='Изменить клиента'>
      <ModalContainer>
        <InputColumn>
          <Controller
            name='organizationalForm'
            control={control}
            render={({ field }) => (
              <Select
                placeholder='Выберите орг. форму'
                label='Орг. форма'
                error={errors.organizationalForm?.message}
                data={organizationalFormOptions}
                {...field}
              />
            )}
          />

          <Controller
            name='name'
            control={control}
            rules={{ required: 'Название обязательно' }}
            render={({ field }) => (
              <Input
                placeholder='Руснедра Менеджмент'
                label={'Наименование'}
                error={errors.name?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='inn'
            control={control}
            rules={{ required: 'ИНН обязателен' }}
            render={({ field }) => (
              <Input
                placeholder='ИНН'
                label={'ИНН'}
                error={errors.inn?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='kpp'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='КПП'
                label={'КПП'}
                error={errors.kpp?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='ogrn'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='ОГРН'
                label='ОГРН'
                error={errors.ogrn?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='headPosition'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Должность руководителя'
                label='Должность'
                error={errors.headPosition?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='director'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='ФИО руководителя'
                label={'ФИО руководителя'}
                error={errors.director?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='headPositionGenitive'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Должность в родительном падеже (генерального директора)'
                label='Должность (род. падеж)'
                error={errors.headPositionGenitive?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='headNameGenitive'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='в лице Кудрявцевой Дарьи Николаевны'
                label='ФИО (род. падеж)'
                error={errors.headNameGenitive?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='offerContactPosition'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Генеральному директору'
                label='Должность для КП (дат. падеж)'
                {...field}
              />
            )}
          />

          <Controller
            name='offerContactName'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Иванову Ивану Ивановичу'
                label='ФИО для КП (дат. падеж)'
                {...field}
              />
            )}
          />

          <Controller
            name='authorityType'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Устава, Доверенности и т.д. (род. падеж)'
                label='Действует на основании'
                error={errors.authorityType?.message}
                {...field}
              />
            )}
          />
        </InputColumn>
        <InputColumn>
          <Controller
            name='legalAddress'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Юридический адрес'
                label={'Юридический адрес'}
                error={errors.legalAddress?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='postAddress'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Почтовый адрес'
                label={'Почтовый адрес'}
                error={errors.postAddress?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='phone'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Телефон'
                label={'Телефон'}
                error={errors.phone?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='email'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='example@company.com'
                label='Email'
                error={errors.email?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='rs'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Расчетный счет'
                label={'Расчетный счет'}
                error={errors.rs?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='bankName'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Название банка'
                label={'Название банка'}
                error={errors.bankName?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='bic'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='БИК'
                label={'БИК'}
                error={errors.bic?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='ks'
            control={control}
            render={({ field }) => (
              <Input
                placeholder='Корреспондентский счет'
                label='Кор. счет'
                error={errors.ks?.message}
                {...field}
              />
            )}
          />
        </InputColumn>
      </ModalContainer>
      <ButtonContainer>
        <Button onClick={handleSubmit(onSubmit)}>Сохранить</Button>
      </ButtonContainer>
    </Modal>
  )
}
